import { useEffect, useRef, useState, useCallback } from 'react';

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;       // 15 minutes
const WARNING_BEFORE_MS = 2 * 60 * 1000;       // Show warning 2 min before timeout
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
const THROTTLE_MS = 30_000;                     // Only update last-active once per 30s

/**
 * Hook that tracks user activity and triggers logout after 15 minutes of inactivity.
 * Shows a warning modal 2 minutes before the session expires.
 *
 * NIST 800-171 3.1.10 — Session lock after period of inactivity.
 *
 * @param {Function} onTimeout — called when idle timeout expires (should clear tokens & redirect)
 * @returns {{ showWarning: boolean, remainingSeconds: number, dismissWarning: Function }}
 */
export default function useIdleTimeout(onTimeout) {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(120);

  const lastActivityRef = useRef(Date.now());
  const timeoutIdRef = useRef(null);
  const warningIntervalRef = useRef(null);
  const throttleRef = useRef(0);

  const resetTimer = useCallback(() => {
    const now = Date.now();
    // Throttle: only process resets every THROTTLE_MS
    if (now - throttleRef.current < THROTTLE_MS) return;
    throttleRef.current = now;

    lastActivityRef.current = now;
    setShowWarning(false);

    // Clear existing timers
    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    if (warningIntervalRef.current) clearInterval(warningIntervalRef.current);

    // Set warning timer (fires 2 min before timeout)
    timeoutIdRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemainingSeconds(Math.floor(WARNING_BEFORE_MS / 1000));

      // Countdown every second
      warningIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - lastActivityRef.current;
        const remaining = Math.max(0, Math.floor((IDLE_TIMEOUT_MS - elapsed) / 1000));
        setRemainingSeconds(remaining);

        if (remaining <= 0) {
          clearInterval(warningIntervalRef.current);
          onTimeout();
        }
      }, 1000);
    }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);
  }, [onTimeout]);

  const dismissWarning = useCallback(() => {
    // User interacted with warning — reset everything
    throttleRef.current = 0; // bypass throttle for explicit dismiss
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    // Start the timer on mount
    resetTimer();

    // Attach activity listeners
    const handleActivity = () => resetTimer();
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
      if (warningIntervalRef.current) clearInterval(warningIntervalRef.current);
    };
  }, [resetTimer]);

  return { showWarning, remainingSeconds, dismissWarning };
}
